import { IPD_TIMED_FREQUENCIES, DEFAULT_IPD_DOSE_TIMES, type IpdTimedFrequency } from '@his/shared';

export const DOSE_TIMES_PER_DAY: Readonly<Record<IpdTimedFrequency, number>> = Object.freeze({
  od: 1,
  bd: 2,
  tds: 3,
  qds: 4,
  hs: 1,
  q4h: 6,
  q6h: 4,
  q8h: 3,
  q12h: 2,
  q24h: 1,
});

export const MEAL_TIMES_MAX = 6;
export const VITALS_INTERVAL_CHOICES = [0, 60, 120, 240, 360, 480, 720] as const;

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

export function minuteOfDay(time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}

export function sortTimes(times: readonly string[]): string[] {
  return [...new Set(times.filter((time) => TIME_OF_DAY.test(time)))].sort((a, b) => minuteOfDay(a) - minuteOfDay(b));
}

export function withTime(times: readonly string[], time: string, max: number): string[] {
  if (!TIME_OF_DAY.test(time) || times.includes(time) || times.length >= max) return sortTimes(times);
  return sortTimes([...times, time]);
}

export function withoutTime(times: readonly string[], time: string): string[] {
  return sortTimes(times.filter((entry) => entry !== time));
}

export interface DoseTimesProblem {
  code: IpdTimedFrequency;
  expected: number;
  actual: number;
}

export function doseTimesProblem(code: IpdTimedFrequency, times: readonly string[]): DoseTimesProblem | null {
  const actual = sortTimes(times).length;
  const expected = DOSE_TIMES_PER_DAY[code];
  return actual === expected ? null : { code, expected, actual };
}

export function doseTimesProblems(doseTimes: Readonly<Record<IpdTimedFrequency, readonly string[]>>): DoseTimesProblem[] {
  return IPD_TIMED_FREQUENCIES.map((code) => doseTimesProblem(code, doseTimes[code] ?? [])).filter((problem): problem is DoseTimesProblem => problem !== null);
}

export function doseTimesPatch(
  current: Readonly<Record<IpdTimedFrequency, readonly string[]>>,
  code: IpdTimedFrequency,
  times: readonly string[],
): Record<IpdTimedFrequency, string[]> {
  return Object.fromEntries(IPD_TIMED_FREQUENCIES.map((entry) => [entry, sortTimes(entry === code ? times : (current[entry] ?? DEFAULT_IPD_DOSE_TIMES[entry]))])) as Record<
    IpdTimedFrequency,
    string[]
  >;
}

export function isDefaultTimes(times: readonly string[], defaults: readonly string[]): boolean {
  const sorted = sortTimes(times);
  const expected = sortTimes(defaults);
  return sorted.length === expected.length && sorted.every((time, index) => time === expected[index]);
}

export function wardIntervalsPatch(current: Readonly<Record<string, number>>, wardId: string, minutes: number | null): Record<string, number> {
  const next: Record<string, number> = { ...current };
  if (minutes === null) delete next[wardId];
  else next[wardId] = minutes;
  return next;
}

export function intervalParts(minutes: number): { kind: 'off' } | { kind: 'hours'; hours: number } | { kind: 'minutes'; minutes: number } {
  if (minutes === 0) return { kind: 'off' };
  if (minutes % 60 === 0) return { kind: 'hours', hours: minutes / 60 };
  return { kind: 'minutes', minutes };
}
